import { apiFetch } from './client';

const BASE = '/customers';

/**
 * List customers (paged).
 * @param {{ page?: number, size?: number, sort?: string, isActive?: boolean, signal?: AbortSignal }} [opts]
 *   sort is `property,direction` (e.g. 'lastName,asc'); overridable per call.
 *   isActive maps to the `is_active` query param. Omit to accept the server default
 *   (currently active-only); pass explicitly to insulate callers from server-default changes.
 * @returns {Promise<{ content: Array, page: object }>}
 */
export const list = ({ page = 0, size = 20, sort = 'lastName,asc', isActive, signal } = {}) => {
  let qs = `page=${page}&size=${size}&sort=${sort}`;
  if (isActive !== undefined) qs += `&is_active=${isActive}`;
  return apiFetch(`${BASE}?${qs}`, { signal });
};

/**
 * Fetch a single customer by id.
 * @param {string} id
 * @param {{ signal?: AbortSignal }} [opts]
 */
export const get = (id, { signal } = {}) =>
  apiFetch(`${BASE}/${id}`, { signal });

/**
 * Create a customer.
 * @param {object} body
 * @param {{ signal?: AbortSignal }} [opts]
 */
export const create = (body, { signal } = {}) =>
  apiFetch(BASE, { method: 'POST', body: JSON.stringify(body), signal });

/**
 * Partial-update a customer.
 * @param {string} id
 * @param {object} body
 * @param {{ signal?: AbortSignal }} [opts]
 */
export const update = (id, body, { signal } = {}) =>
  apiFetch(`${BASE}/${id}`, { method: 'PATCH', body: JSON.stringify(body), signal });

/**
 * Delete a customer.
 * @param {string} id
 * @param {{ signal?: AbortSignal }} [opts]
 */
export const remove = (id, { signal } = {}) =>
  apiFetch(`${BASE}/${id}`, { method: 'DELETE', signal });

/**
 * List a customer's attribute values.
 * @param {string} customerId
 * @param {{ signal?: AbortSignal }} [opts]
 */
export const listAttributes = (customerId, { signal } = {}) =>
  apiFetch(`${BASE}/${customerId}/attributes`, { signal });

/**
 * Replace a customer's attribute values (PUT semantics).
 * @param {string} customerId
 * @param {Array} values
 * @param {{ signal?: AbortSignal }} [opts]
 */
export const saveAttributes = (customerId, values, { signal } = {}) =>
  apiFetch(`${BASE}/${customerId}/attributes`, {
    method: 'PUT',
    body: JSON.stringify(values),
    signal,
  });

/**
 * List a customer's product instances (paged).
 * @param {string} customerId
 * @param {{ page?: number, size?: number, signal?: AbortSignal }} [opts]
 */
export const listProductInstances = (customerId, { page = 0, size = 20, signal } = {}) =>
  apiFetch(`${BASE}/${customerId}/product_instances?page=${page}&size=${size}`, { signal });

/**
 * Create a new product instance for a customer.
 * @param {string} customerId
 * @param {string} productId
 * @param {{ signal?: AbortSignal }} [opts]
 */
export const createProductInstance = (customerId, productId, { signal } = {}) =>
  apiFetch(`${BASE}/${customerId}/product_instances`, {
    method: 'POST',
    body: JSON.stringify({ product_id: productId }),
    signal,
  });

/**
 * Delete a product instance.
 * @param {string} customerId
 * @param {string} productInstanceId
 * @param {{ signal?: AbortSignal }} [opts]
 */
export const removeProductInstance = (customerId, productInstanceId, { signal } = {}) =>
  apiFetch(`${BASE}/${customerId}/product_instances/${productInstanceId}`, {
    method: 'DELETE',
    signal,
  });

/**
 * Fetch a single product instance for a customer.
 * @param {string} customerId
 * @param {string} productInstanceId
 * @param {{ signal?: AbortSignal }} [opts]
 */
export const getProductInstance = (customerId, productInstanceId, { signal } = {}) =>
  apiFetch(`${BASE}/${customerId}/product_instances/${productInstanceId}`, { signal });

/**
 * List attribute values for a customer's product instance.
 * @param {string} customerId
 * @param {string} productInstanceId
 * @param {{ signal?: AbortSignal }} [opts]
 */
export const listProductInstanceAttributes = (customerId, productInstanceId, { signal } = {}) =>
  apiFetch(`${BASE}/${customerId}/product_instances/${productInstanceId}/attributes`, { signal });

/**
 * Replace attribute values for a customer's product instance (PUT semantics).
 * @param {string} customerId
 * @param {string} productInstanceId
 * @param {Array} values
 * @param {{ signal?: AbortSignal }} [opts]
 */
export const saveProductInstanceAttributes = (customerId, productInstanceId, values, { signal } = {}) =>
  apiFetch(`${BASE}/${customerId}/product_instances/${productInstanceId}/attributes`, {
    method: 'PUT',
    body: JSON.stringify(values),
    signal,
  });

/**
 * Replace a single contact-level attribute value (PUT semantics).
 * @param {string} customerId
 * @param {string} attributeId
 * @param {object} value
 * @param {{ signal?: AbortSignal }} [opts]
 */
export const saveAttribute = (customerId, attributeId, value, { signal } = {}) =>
  apiFetch(`${BASE}/${customerId}/attributes/${attributeId}`, {
    method: 'PUT',
    body: JSON.stringify(value),
    signal,
  });

/**
 * Replace a single product-instance attribute value (PUT semantics).
 * @param {string} customerId
 * @param {string} productInstanceId
 * @param {string} attributeId
 * @param {object} value
 * @param {{ signal?: AbortSignal }} [opts]
 */
export const saveProductInstanceAttribute = (customerId, productInstanceId, attributeId, value, { signal } = {}) =>
  apiFetch(`${BASE}/${customerId}/product_instances/${productInstanceId}/attributes/${attributeId}`, {
    method: 'PUT',
    body: JSON.stringify(value),
    signal,
  });
